/**
 * Learner feedback (after a lesson, and after the whole course when `lessonCode` is omitted).
 * Server: services/astro-bot/src/api/routes/course-student.ts — GET/POST /api/v1/course/feedback,
 * DELETE /api/v1/course/feedback/:id (204). Failures are `CourseApiError`s: branch on `.code`
 * (429 `feedback_limit`, 403 `locked` / `not_launched`, 400 `invalid_body`).
 *
 * Plain state instead of react-query: the list belongs to this one form, nothing else reads it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';
import type { FeedbackDto, FeedbackInput, FeedbackListDto } from '../types/course';
import { CourseApiError } from './useCourse';

async function feedbackRequest<T>(jwt: string | null, path: string, method: string, payload?: unknown): Promise<T> {
  if (!jwt) throw new Error('not_authenticated');
  const init: RequestInit = { method };
  if (payload !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(payload);
  }
  const res = await apiFetch(path, jwt, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new CourseApiError(res.status, typeof body.error === 'string' ? body.error : '', body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface CourseFeedback {
  /** Own feedback to this lesson (or to the course), newest first. Empty until loaded or when the load failed. */
  items: FeedbackDto[];
  /** Sends one feedback and refreshes the list. Throws a `CourseApiError` on a server refusal. */
  submit: (input: Omit<FeedbackInput, 'lessonCode'>) => Promise<void>;
  /** Deletes one own feedback and refreshes the list (also when it was already gone). */
  remove: (id: string) => Promise<void>;
  submitting: boolean;
  removing: boolean;
}

export function useCourseFeedback(lessonCode?: string): CourseFeedback {
  const jwt = useAuth((s) => s.jwt);
  const [items, setItems] = useState<FeedbackDto[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const mounted = useRef(true);
  const latest = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    if (!jwt) return;
    const n = ++latest.current;
    try {
      const res = await feedbackRequest<FeedbackListDto>(
        jwt,
        `/api/v1/course/feedback${lessonCode ? `?lesson=${encodeURIComponent(lessonCode)}` : ''}`,
        'GET',
      );
      if (mounted.current && n === latest.current) setItems(Array.isArray(res?.items) ? res.items : []);
    } catch { /* the list is a convenience: the form works without it */ }
  }, [jwt, lessonCode]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => { mounted.current = false; };
  }, [load]);

  const submit = useCallback(async (input: Omit<FeedbackInput, 'lessonCode'>): Promise<void> => {
    setSubmitting(true);
    try {
      await feedbackRequest<FeedbackDto>(jwt, '/api/v1/course/feedback', 'POST', { ...input, ...(lessonCode ? { lessonCode } : {}) });
      await load();
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  }, [jwt, lessonCode, load]);

  const remove = useCallback(async (id: string): Promise<void> => {
    setRemoving(true);
    try {
      await feedbackRequest<void>(jwt, `/api/v1/course/feedback/${encodeURIComponent(id)}`, 'DELETE');
    } finally {
      // Gone already (404) is as good as deleted: refresh either way.
      await load();
      if (mounted.current) setRemoving(false);
    }
  }, [jwt, load]);

  return { items, submit, remove, submitting, removing };
}
